/**
 * NotificationDropdown Component
 *
 * Dropdown panel showing recent notifications.
 * Displays list of notifications, empty state, and action buttons.
 *
 * Features:
 * - List of 10 most recent notifications
 * - "Mark all as read" button
 * - "View all" link to full notification center
 * - Empty state with friendly message
 * - Loading state
 * - Scrollable notification list
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * <NotificationDropdown
 *   notifications={notifications}
 *   onClose={() => setIsOpen(false)}
 *   onMarkAsRead={handleMarkAsRead}
 * />
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationItem from './NotificationItem';

/**
 * NotificationDropdown Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.notifications - Array of notification objects
 * @param {Function} props.onClose - Callback when closing dropdown
 * @param {Function} props.onMarkAsRead - Callback when marking as read
 * @param {Function} props.onMarkAllAsRead - Callback when marking all as read
 * @param {Function} props.onDelete - Callback when deleting notification
 * @param {boolean} props.loading - Loading state
 */
export default function NotificationDropdown({
  notifications = [],
  onClose,
  onMarkAsRead,
  onMarkAllAsRead: _onMarkAllAsRead,
  onDelete,
  loading = false,
}) {
  const navigate = useNavigate();

  // Take only 10 most recent notifications
  const recentNotifications = notifications.slice(0, 10);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />

      {/* Dropdown panel - 320px width, 12px border radius, exact shadow per mockup spec */}
      <div
        className="absolute right-0 mt-2 bg-white border border-gray-200 z-50 overflow-hidden"
        style={{
          width: '320px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header - 16px padding, shows unread count per mockup spec */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <span className="font-semibold text-gray-900">Notifications</span>

          {/* Unread count indicator - 12px, primary color per mockup spec */}
          {unreadCount > 0 && (
            <span className="text-brand-primary font-medium" style={{ fontSize: '12px' }}>
              {unreadCount} new
            </span>
          )}
        </div>

        {/* Notification list - 300px max height per mockup spec */}
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {loading ? (
            // Loading state
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
            </div>
          ) : recentNotifications.length === 0 ? (
            // Empty state - 32px vertical padding, centered, no icon per mockup spec
            <div className="text-center text-gray-500 py-8 px-4" style={{ fontSize: '14px' }}>
              No notifications
            </div>
          ) : (
            // Notification list
            <div>
              {recentNotifications.map((notification, idx) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                  onDelete={onDelete}
                  showActions={false} // Hide actions in dropdown (cleaner UI)
                  isLast={idx === recentNotifications.length - 1}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer - 12px vertical padding, 16px horizontal, 13px font size per mockup spec */}
        <div className="px-4 py-3 border-t border-gray-200 text-center">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/notifications');
            }}
            className="text-brand-primary font-medium hover:text-brand-primary-dark transition-colors"
            style={{ fontSize: '13px' }}
          >
            View all notifications
          </button>
        </div>
      </div>
    </>
  );
}
